import React, { useState, useEffect, useRef } from 'react';
import { 
  UtensilsCrossed, Star, Plus, Trash2, ExternalLink, Clock, DollarSign,
  MessageCircle, Send, Loader2, X, MapPin, Heart, TrendingUp, Pizza,
  Coffee, Salad, Beef, Fish, Cake, ChevronRight, Sparkles, History,
  Smartphone, Play, Edit2, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from '../services/api';
import { useAuth } from '../context/UserAuthContext';

const FoodPage = () => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('order'); // order, favorites, history, shortcuts
  
  // Quick Order Shortcuts state
  const [shortcuts, setShortcuts] = useState(() => {
    const saved = localStorage.getItem('food_shortcuts');
    return saved ? JSON.parse(saved) : [];
  });
  const [showAddShortcut, setShowAddShortcut] = useState(false);
  const [editingShortcut, setEditingShortcut] = useState(null);
  const [newShortcut, setNewShortcut] = useState({
    name: '',
    shortcutName: '',
    webhookUrl: '',
    icon: '🍔',
    color: 'orange'
  });
  const [triggeringShortcut, setTriggeringShortcut] = useState(null); // id of shortcut being triggered
  const [showInstructions, setShowInstructions] = useState(false);
  
  // Save shortcuts to localStorage
  useEffect(() => {
    localStorage.setItem('food_shortcuts', JSON.stringify(shortcuts));
  }, [shortcuts]);
  
  // AI Chat state
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: "Hey! I'm your food assistant. Tell me what you're craving and I'll help you decide what to order! 🍕" }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  
  // Add favorite modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFavorite, setEditingFavorite] = useState(null); // For edit mode
  const [newFavorite, setNewFavorite] = useState({
    name: '', cuisine: '', address: '', image_url: '',
    uber_eats_link: '', doordash_link: '', grubhub_link: '',
    notes: '', usual_order: ''
  });
  
  // Log order modal
  const [showLogModal, setShowLogModal] = useState(false);
  const [newOrder, setNewOrder] = useState({
    restaurant_name: '', items: '', total_amount: '',
    delivery_service: 'uber_eats', notes: '', rating: 5
  });

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [favRes, ordersRes, statsRes] = await Promise.all([
        apiCall('/food/favorites'),
        apiCall('/food/orders?limit=20'),
        apiCall('/food/stats')
      ]);
      
      if (favRes.success) setFavorites(favRes.favorites);
      if (ordersRes.success) setOrders(ordersRes.orders);
      if (statsRes.success) setStats(statsRes.stats);
    } catch (error) {
      console.error('Error fetching food data:', error);
    } finally {
      setLoading(false);
    }
  };

  // AI Chat handler
  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatInput('');
    setChatLoading(true);

    try {
      // Use the existing AI chat endpoint
      const response = await apiCall('/chat/query', {
        method: 'POST',
        body: JSON.stringify({
          message: `You are a helpful food ordering assistant. The user wants help deciding what to eat or order. Be friendly, suggest specific cuisines or dishes, and help them make a decision. Keep responses concise (2-3 sentences). User message: ${userMessage}`,
          provider: 'openai'
        })
      });

      if (response.response) {
        setChatMessages(prev => [...prev, { role: 'assistant', content: response.response }]);
      }
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Sorry, I couldn't process that. But hey, you can never go wrong with pizza! 🍕" 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Add favorite
  const handleAddFavorite = async () => {
    if (!newFavorite.name) {
      toast.error('Restaurant name is required');
      return;
    }

    try {
      const response = await apiCall('/food/favorites', {
        method: 'POST',
        body: JSON.stringify(newFavorite)
      });

      if (response.success) {
        setFavorites(prev => [response.favorite, ...prev]);
        setShowAddModal(false);
        setNewFavorite({
          name: '', cuisine: '', address: '', image_url: '',
          uber_eats_link: '', doordash_link: '', grubhub_link: '',
          notes: '', usual_order: ''
        });
        toast.success('Favorite added!');
      }
    } catch (error) {
      toast.error('Failed to add favorite');
    }
  };

  // Edit favorite
  const handleEditFavorite = (favorite) => {
    setEditingFavorite(favorite);
    setNewFavorite({
      name: favorite.name || '',
      cuisine: favorite.cuisine || '',
      address: favorite.address || '',
      image_url: favorite.image_url || '',
      uber_eats_link: favorite.uber_eats_link || '',
      doordash_link: favorite.doordash_link || '',
      grubhub_link: favorite.grubhub_link || '',
      notes: favorite.notes || '',
      usual_order: favorite.usual_order || ''
    });
    setShowAddModal(true);
  };

  // Update favorite
  const handleUpdateFavorite = async () => {
    if (!newFavorite.name) {
      toast.error('Restaurant name is required');
      return;
    }

    try {
      const response = await apiCall(`/food/favorites/${editingFavorite.id}`, {
        method: 'PUT',
        body: JSON.stringify(newFavorite)
      });

      if (response.success) {
        setFavorites(prev => prev.map(f => 
          f.id === editingFavorite.id ? response.favorite : f
        ));
        setShowAddModal(false);
        setEditingFavorite(null);
        setNewFavorite({
          name: '', cuisine: '', address: '', image_url: '',
          uber_eats_link: '', doordash_link: '', grubhub_link: '',
          notes: '', usual_order: ''
        });
        toast.success('Favorite updated!');
      }
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  // Close modal and reset state
  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingFavorite(null);
    setNewFavorite({
      name: '', cuisine: '', address: '', image_url: '',
      uber_eats_link: '', doordash_link: '', grubhub_link: '',
      notes: '', usual_order: ''
    });
  };

  // Delete favorite
  const handleDeleteFavorite = async (id) => {
    try {
      await apiCall(`/food/favorites/${id}`, { method: 'DELETE' });
      setFavorites(prev => prev.filter(f => f.id !== id));
      toast.success('Favorite removed');
    } catch (error) {
      toast.error('Failed to remove favorite');
    }
  };

  // Log order
  const handleLogOrder = async () => {
    if (!newOrder.restaurant_name || !newOrder.items) {
      toast.error('Restaurant and items are required');
      return;
    }

    try {
      const orderData = {
        ...newOrder,
        total_amount: newOrder.total_amount ? parseFloat(newOrder.total_amount) : null,
        rating: parseInt(newOrder.rating)
      };

      const response = await apiCall('/food/orders', {
        method: 'POST',
        body: JSON.stringify(orderData)
      });

      if (response.success) {
        setOrders(prev => [response.order, ...prev]);
        setShowLogModal(false);
        setNewOrder({
          restaurant_name: '', items: '', total_amount: '',
          delivery_service: 'uber_eats', notes: '', rating: 5
        });
        toast.success('Order logged!');
        // Refresh stats
        const statsRes = await apiCall('/food/stats');
        if (statsRes.success) setStats(statsRes.stats);
      }
    } catch (error) {
      toast.error('Failed to log order');
    }
  };

  // Quick order links
  const deliveryApps = [
    { id: 'uber_eats', name: 'Uber Eats', color: '#06C167', url: 'https://www.ubereats.com', icon: '🥡' },
    { id: 'doordash', name: 'DoorDash', color: '#FF3008', url: 'https://www.doordash.com', icon: '🚗' },
    { id: 'grubhub', name: 'Grubhub', color: '#F63440', url: 'https://www.grubhub.com', icon: '🍔' },
  ];

  // Shortcut functions
  const runShortcut = async (shortcut) => {
    // If webhook URL is configured, always trigger via backend proxy (works on desktop + phone)
    if (shortcut.webhookUrl) {
      setTriggeringShortcut(shortcut.id);
      try {
        await apiCall('/pushcut/trigger', {
          method: 'POST',
          body: JSON.stringify({ webhook_url: shortcut.webhookUrl })
        });
        toast.success(`Triggered: ${shortcut.name}! Check your iPhone.`);
      } catch (err) {
        const msg = err?.message || 'Failed to trigger shortcut';
        toast.error(msg);
      } finally {
        setTriggeringShortcut(null);
      }
      return;
    }
    // Fallback: iOS URL scheme (only works when running on iPhone/iPad)
    const encodedName = encodeURIComponent(shortcut.shortcutName || shortcut.name);
    window.location.href = `shortcuts://run-shortcut?name=${encodedName}`;
    toast.success(`Running shortcut: ${shortcut.shortcutName || shortcut.name}`);
  };

  const addShortcut = () => {
    if (!newShortcut.name || !newShortcut.shortcutName) {
      toast.error('Please fill in all fields');
      return;
    }
    
    const shortcut = {
      id: Date.now().toString(),
      ...newShortcut
    };
    
    setShortcuts(prev => [...prev, shortcut]);
    setNewShortcut({ name: '', shortcutName: '', webhookUrl: '', icon: '🍔', color: 'orange' });
    setShowAddShortcut(false);
    toast.success('Shortcut added!');
  };

  const updateShortcut = () => {
    if (!editingShortcut) return;
    
    setShortcuts(prev => prev.map(s => 
      s.id === editingShortcut.id ? editingShortcut : s
    ));
    setEditingShortcut(null);
    toast.success('Shortcut updated!');
  };

  const deleteShortcut = (id) => {
    setShortcuts(prev => prev.filter(s => s.id !== id));
    toast.success('Shortcut removed');
  };

  const shortcutColors = {
    orange: 'from-orange-500 to-red-500',
    green: 'from-green-500 to-emerald-600',
    blue: 'from-blue-500 to-indigo-600',
    purple: 'from-purple-500 to-pink-500',
    yellow: 'from-yellow-500 to-orange-500',
    red: 'from-red-500 to-rose-600',
  };

  const shortcutIcons = ['🍔', '🍕', '🌮', '🍜', '🍣', '🥗', '🍱', '☕', '🧋', '🍦', '🌯', '🥡'];

  const cuisineTypes = [
    { name: 'Pizza', icon: Pizza, color: '#FF6B35' },
    { name: 'Coffee', icon: Coffee, color: '#8B4513' },
    { name: 'Salad', icon: Salad, color: '#32CD32' },
    { name: 'Steak', icon: Beef, color: '#8B0000' },
    { name: 'Seafood', icon: Fish, color: '#4169E1' },
    { name: 'Dessert', icon: Cake, color: '#FF69B4' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
            <UtensilsCrossed className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Food Hub</h1>
            <p className="text-gray-400 text-sm">Order food from your dashboard</p>
          </div>
        </div>
        
        <button
          onClick={() => setShowLogModal(true)}
          className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={18} />
          Log Order
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-black border border-gray-400 rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <History size={14} />
              Total Orders
            </div>
            <div className="text-2xl font-bold">{stats.total_orders}</div>
          </div>
          <div className="bg-black border border-gray-400 rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <DollarSign size={14} />
              Total Spent
            </div>
            <div className="text-2xl font-bold">${stats.total_spent}</div>
          </div>
          <div className="bg-black border border-gray-400 rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <TrendingUp size={14} />
              Avg Order
            </div>
            <div className="text-2xl font-bold">${stats.avg_order_value}</div>
          </div>
          <div className="bg-black border border-gray-400 rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Heart size={14} />
              Favorites
            </div>
            <div className="text-2xl font-bold">{stats.favorites_count}</div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-gray-700 pb-4 overflow-x-auto">
        {[
          { id: 'order', label: 'Order Now', icon: UtensilsCrossed },
          { id: 'shortcuts', label: 'Quick Order', icon: Smartphone },
          { id: 'favorites', label: 'Favorites', icon: Heart },
          { id: 'history', label: 'History', icon: History },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id 
                ? 'bg-orange-500 text-white' 
                : 'bg-black text-gray-400 hover:bg-gray-900'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Order Now Tab */}
      {activeTab === 'order' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Quick Links & AI Chat */}
          <div className="space-y-6">
            {/* Quick Launch Apps */}
            <div className="bg-black border border-gray-400 rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <ExternalLink size={18} />
                Quick Order
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {deliveryApps.map(app => (
                  <a
                    key={app.id}
                    href={app.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-2 p-4 bg-black rounded-xl hover:scale-105 transition-transform border border-gray-400 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)]"
                  >
                    <span className="text-3xl">{app.icon}</span>
                    <span className="text-sm font-medium">{app.name}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Cuisine Quick Picks */}
            <div className="bg-black border border-gray-400 rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Sparkles size={18} />
                What are you craving?
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {cuisineTypes.map(cuisine => (
                  <button
                    key={cuisine.name}
                    onClick={() => setChatInput(`I'm craving ${cuisine.name.toLowerCase()}`)}
                    className="flex flex-col items-center gap-2 p-3 bg-black rounded-xl hover:scale-105 transition-transform border border-gray-400 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)]"
                  >
                    <cuisine.icon size={24} style={{ color: cuisine.color }} />
                    <span className="text-xs">{cuisine.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* AI Food Assistant */}
            <div className="bg-black border border-gray-400 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
              <div className="p-4 border-b border-gray-700 flex items-center gap-2">
                <MessageCircle size={18} className="text-orange-500" />
                <h3 className="font-semibold">AI Food Assistant</h3>
              </div>
              
              {/* Chat Messages */}
              <div className="h-64 overflow-y-auto p-4 space-y-3">
                {chatMessages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-800 text-gray-100'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-800 px-4 py-2 rounded-2xl">
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input */}
              <form onSubmit={handleChatSubmit} className="p-4 border-t border-gray-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="What should I eat today?"
                    className="flex-1 bg-black border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
                  />
                  <button
                    type="submit"
                    disabled={chatLoading}
                    className="p-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Send size={20} />
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Right Column - Favorites Quick Access */}
          <div className="space-y-6">
            <div className="bg-black border border-gray-400 rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Star size={18} className="text-yellow-500" />
                  Your Favorites
                </h3>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="text-sm text-orange-500 hover:text-orange-400 flex items-center gap-1"
                >
                  <Plus size={14} />
                  Add
                </button>
              </div>

              {favorites.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Heart size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No favorites yet</p>
                  <p className="text-sm">Add your go-to restaurants</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {favorites.map(fav => (
                    <div
                      key={fav.id}
                      className="bg-black border border-gray-400 rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold">{fav.name}</h4>
                          {fav.cuisine && (
                            <p className="text-sm text-gray-400">{fav.cuisine}</p>
                          )}
                          {fav.usual_order && (
                            <p className="text-xs text-orange-400 mt-1">
                              Usual: {fav.usual_order}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteFavorite(fav.id)}
                          className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      
                      {/* Quick order links */}
                      <div className="flex gap-2 mt-3">
                        {fav.uber_eats_link && (
                          <a
                            href={fav.uber_eats_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 bg-[#06C167] text-white text-xs rounded-full hover:opacity-80"
                          >
                            Uber Eats
                          </a>
                        )}
                        {fav.doordash_link && (
                          <a
                            href={fav.doordash_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 bg-[#FF3008] text-white text-xs rounded-full hover:opacity-80"
                          >
                            DoorDash
                          </a>
                        )}
                        {fav.grubhub_link && (
                          <a
                            href={fav.grubhub_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 bg-[#F63440] text-white text-xs rounded-full hover:opacity-80"
                          >
                            Grubhub
                          </a>
                        )}
                        {!fav.uber_eats_link && !fav.doordash_link && !fav.grubhub_link && (
                          <span className="text-xs text-gray-500">No quick links added</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Orders Preview */}
            {orders.length > 0 && (
              <div className="bg-black border border-gray-400 rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Clock size={18} />
                  Recent Orders
                </h3>
                <div className="space-y-2">
                  {orders.slice(0, 3).map(order => (
                    <div
                      key={order.id}
                      className="flex items-center justify-between p-3 bg-black rounded-lg border border-gray-700"
                    >
                      <div>
                        <p className="font-medium text-sm">{order.restaurant_name}</p>
                        <p className="text-xs text-gray-500">{order.items}</p>
                      </div>
                      {order.total_amount && (
                        <span className="text-sm font-semibold text-green-400">
                          ${order.total_amount}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Order Shortcuts Tab */}
      {activeTab === 'shortcuts' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-semibold">Quick Order Shortcuts</h2>
              <p className="text-sm text-gray-400">Tap to run iOS Shortcuts for instant ordering</p>
            </div>
            <button
              onClick={() => setShowAddShortcut(true)}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg flex items-center gap-2 text-sm"
            >
              <Plus size={16} />
              Add Shortcut
            </button>
          </div>

          {/* Instructions - Collapsible */}
          <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 border border-blue-700/50 rounded-xl mb-6 overflow-hidden">
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
            >
              <h3 className="font-semibold flex items-center gap-2 text-lg">
                <Smartphone size={20} className="text-blue-400" />
                How to Set Up Remote Shortcuts (Desktop + iPhone)
              </h3>
              <ChevronRight 
                size={20} 
                className={`text-gray-400 transition-transform duration-200 ${showInstructions ? 'rotate-90' : ''}`} 
              />
            </button>
            
            {showInstructions && (
              <div className="px-5 pb-5 space-y-4">
                {/* Intro */}
                <div className="p-3 bg-green-900/30 border border-green-700/50 rounded-lg">
                  <p className="text-sm text-green-200">
                    <strong>Pushcut Remote Triggering:</strong> Add a Pushcut Webhook URL to any shortcut button so you can fire it directly from your desktop — no phone required to tap.
                  </p>
                </div>

                {/* Step 1 */}
                <div className="bg-black/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 bg-green-500 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Open Pushcut on your iPhone</h4>
                      <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
                        <li>Tap <strong>Notifications</strong> → tap <strong>+</strong> to create a new notification</li>
                        <li>Give it a name — e.g. <strong>"Order Chipotle"</strong></li>
                        <li>Under <strong>Actions</strong>, tap <strong>Add Action</strong></li>
                        <li>Choose <strong>Run Shortcut</strong> → pick your iOS Shortcut (e.g. "Order Chipotle")</li>
                        <li>Tap <strong>Save</strong></li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="bg-black/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 bg-green-500 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Copy your Webhook URL</h4>
                      <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
                        <li>In Pushcut, open the notification you just created</li>
                        <li>Tap the <strong>share / link icon</strong> or look for <strong>"Webhook URL"</strong></li>
                        <li>Copy the URL — it looks like:</li>
                      </ul>
                      <div className="mt-2 bg-black/50 border border-gray-700 rounded-lg p-2">
                        <code className="text-xs text-green-300 break-all">https://api.pushcut.io/sk_xxxxxxxx/notifications/Order%20Chipotle</code>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">The <code className="text-gray-400">sk_</code> part is your secret — keep it private.</p>
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="bg-black/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 bg-green-500 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Paste into OPERATR</h4>
                      <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
                        <li>Click <strong>"Add Shortcut"</strong> above</li>
                        <li>Fill in the restaurant name and icon</li>
                        <li>Paste the Pushcut Webhook URL into the <strong>Pushcut Webhook URL</strong> field</li>
                        <li>Click <strong>Add Shortcut</strong> — done!</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="bg-black/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 bg-green-500 rounded-full flex items-center justify-center text-sm font-bold">4</span>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Trigger from desktop or iPhone</h4>
                      <p className="text-sm text-gray-300">Click any shortcut button with a webhook URL — OPERATR sends the request to Pushcut, which pushes a notification to your iPhone and runs your Shortcut automatically.</p>
                      <div className="mt-2 p-2 bg-green-900/20 border border-green-700/40 rounded-lg">
                        <p className="text-xs text-green-300">Buttons showing <strong>"Pushcut connected"</strong> work from any device. Buttons without a webhook URL only work when tapped on iPhone.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-yellow-900/30 border border-yellow-700/50 rounded-lg">
                  <p className="text-sm text-yellow-200 font-semibold mb-2">Pro Tips:</p>
                  <ul className="text-sm text-yellow-200/80 list-disc list-inside space-y-1">
                    <li>One Pushcut notification per restaurant — give each a memorable name</li>
                    <li>DoorDash deep link in your Shortcut: <strong className="font-mono">doordash://</strong></li>
                    <li>Uber Eats deep link: <strong className="font-mono">ubereats://</strong></li>
                    <li>Your Pushcut secret (<code>sk_...</code>) is in Pushcut → Account → Webhook Secret</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
          {shortcuts.length === 0 ? (
            <div className="bg-black border border-gray-400 rounded-xl p-12 text-center hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
              <Smartphone size={48} className="mx-auto mb-4 text-gray-600" />
              <h3 className="text-lg font-semibold mb-2">No Shortcuts Yet</h3>
              <p className="text-gray-400 mb-4">Add shortcuts to quickly reorder from your favorite restaurants</p>
              <button
                onClick={() => setShowAddShortcut(true)}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg inline-flex items-center gap-2"
              >
                <Plus size={16} />
                Add Your First Shortcut
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {shortcuts.map(shortcut => (
                <div
                  key={shortcut.id}
                  className="relative group"
                >
                  <button
                    onClick={() => runShortcut(shortcut)}
                    disabled={triggeringShortcut === shortcut.id}
                    className={`w-full bg-gradient-to-br ${shortcutColors[shortcut.color] || shortcutColors.orange} rounded-2xl p-6 text-center hover:scale-105 transition-transform shadow-lg disabled:opacity-70 disabled:scale-100`}
                    data-testid={`shortcut-${shortcut.id}`}
                  >
                    <div className="text-4xl mb-3">
                      {triggeringShortcut === shortcut.id ? '⏳' : shortcut.icon}
                    </div>
                    <div className="font-bold text-lg">{shortcut.name}</div>
                    <div className="text-xs text-white/70 mt-1 flex items-center justify-center gap-1">
                      {triggeringShortcut === shortcut.id ? (
                        <>
                          <Loader2 size={12} className="animate-spin" />
                          Triggering…
                        </>
                      ) : shortcut.webhookUrl ? (
                        <>
                          <Play size={12} />
                          Pushcut connected
                        </>
                      ) : (
                        <>
                          <Play size={12} />
                          Tap to order
                        </>
                      )}
                    </div>
                  </button>
                  
                  {/* Edit/Delete buttons on hover */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingShortcut(shortcut);
                      }}
                      className="p-1.5 bg-black/50 rounded-lg hover:bg-black/70"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteShortcut(shortcut.id);
                      }}
                      className="p-1.5 bg-black/50 rounded-lg hover:bg-red-500/70"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Shortcut Modal */}
      {showAddShortcut && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-black border border-gray-400 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Quick Order Shortcut</h3>
              <button onClick={() => setShowAddShortcut(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Restaurant Name</label>
                <input
                  type="text"
                  value={newShortcut.name}
                  onChange={(e) => setNewShortcut(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Chipotle, Thai Palace"
                  className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg focus:border-orange-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">iOS Shortcut Name (exact match)</label>
                <input
                  type="text"
                  value={newShortcut.shortcutName}
                  onChange={(e) => setNewShortcut(prev => ({ ...prev, shortcutName: e.target.value }))}
                  placeholder="e.g., Order Chipotle"
                  className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg focus:border-orange-500 focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Must match the name in your Shortcuts app exactly</p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                  Pushcut Webhook URL <span className="text-green-400 text-xs font-semibold ml-1">— enables desktop triggering</span>
                </label>
                <input
                  type="url"
                  value={newShortcut.webhookUrl}
                  onChange={(e) => setNewShortcut(prev => ({ ...prev, webhookUrl: e.target.value }))}
                  placeholder="https://api.pushcut.io/sk_.../notifications/Order%20Chipotle"
                  className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg focus:border-green-500 focus:outline-none font-mono text-xs"
                />
                <p className="text-xs text-gray-500 mt-1">From Pushcut app → Your notification → Copy Webhook URL</p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Icon</label>
                <div className="flex flex-wrap gap-2">
                  {shortcutIcons.map(icon => (
                    <button
                      key={icon}
                      onClick={() => setNewShortcut(prev => ({ ...prev, icon }))}
                      className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-all ${
                        newShortcut.icon === icon 
                          ? 'bg-orange-500 scale-110' 
                          : 'bg-gray-800 hover:bg-gray-700'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Color</label>
                <div className="flex gap-2">
                  {Object.entries(shortcutColors).map(([name, gradient]) => (
                    <button
                      key={name}
                      onClick={() => setNewShortcut(prev => ({ ...prev, color: name }))}
                      className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} transition-all ${
                        newShortcut.color === name ? 'ring-2 ring-white scale-110' : ''
                      }`}
                    />
                  ))}
                </div>
              </div>

              <button
                onClick={addShortcut}
                className="w-full py-3 bg-orange-500 hover:bg-orange-600 rounded-lg font-semibold flex items-center justify-center gap-2"
              >
                <Plus size={18} />
                Add Shortcut
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Shortcut Modal */}
      {editingShortcut && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-black border border-gray-400 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Shortcut</h3>
              <button onClick={() => setEditingShortcut(null)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Restaurant Name</label>
                <input
                  type="text"
                  value={editingShortcut.name}
                  onChange={(e) => setEditingShortcut(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg focus:border-orange-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">iOS Shortcut Name</label>
                <input
                  type="text"
                  value={editingShortcut.shortcutName}
                  onChange={(e) => setEditingShortcut(prev => ({ ...prev, shortcutName: e.target.value }))}
                  className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg focus:border-orange-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                  Pushcut Webhook URL <span className="text-green-400 text-xs font-semibold ml-1">— enables desktop triggering</span>
                </label>
                <input
                  type="url"
                  value={editingShortcut.webhookUrl || ''}
                  onChange={(e) => setEditingShortcut(prev => ({ ...prev, webhookUrl: e.target.value }))}
                  placeholder="https://api.pushcut.io/sk_.../notifications/Order%20Chipotle"
                  className="w-full px-4 py-2 bg-black border border-gray-700 rounded-lg focus:border-green-500 focus:outline-none font-mono text-xs"
                />
                <p className="text-xs text-gray-500 mt-1">From Pushcut app → Your notification → Copy Webhook URL</p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Icon</label>
                <div className="flex flex-wrap gap-2">
                  {shortcutIcons.map(icon => (
                    <button
                      key={icon}
                      onClick={() => setEditingShortcut(prev => ({ ...prev, icon }))}
                      className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-all ${
                        editingShortcut.icon === icon 
                          ? 'bg-orange-500 scale-110' 
                          : 'bg-gray-800 hover:bg-gray-700'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Color</label>
                <div className="flex gap-2">
                  {Object.entries(shortcutColors).map(([name, gradient]) => (
                    <button
                      key={name}
                      onClick={() => setEditingShortcut(prev => ({ ...prev, color: name }))}
                      className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} transition-all ${
                        editingShortcut.color === name ? 'ring-2 ring-white scale-110' : ''
                      }`}
                    />
                  ))}
                </div>
              </div>

              <button
                onClick={updateShortcut}
                className="w-full py-3 bg-orange-500 hover:bg-orange-600 rounded-lg font-semibold flex items-center justify-center gap-2"
              >
                <Check size={18} />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Favorites Tab */}
      {activeTab === 'favorites' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Favorite Restaurants</h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg flex items-center gap-2 text-sm"
            >
              <Plus size={16} />
              Add Favorite
            </button>
          </div>

          {favorites.length === 0 ? (
            <div className="bg-black border border-gray-400 rounded-xl p-12 text-center hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
              <Heart size={48} className="mx-auto mb-4 text-gray-600" />
              <h3 className="text-lg font-semibold mb-2">No favorites yet</h3>
              <p className="text-gray-400 mb-4">Save your go-to restaurants for quick access</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg"
              >
                Add Your First Favorite
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {favorites.map(fav => (
                <div
                  key={fav.id}
                  className="bg-black border border-gray-400 rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{fav.name}</h3>
                      {fav.cuisine && (
                        <span className="text-sm text-orange-400">{fav.cuisine}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEditFavorite(fav)}
                        className="p-2 text-gray-500 hover:text-orange-500 hover:bg-orange-500/10 rounded-lg transition-colors"
                        data-testid={`edit-favorite-${fav.id}`}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteFavorite(fav.id)}
                        className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                        data-testid={`delete-favorite-${fav.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {fav.address && (
                    <p className="text-sm text-gray-400 flex items-center gap-1 mb-2">
                      <MapPin size={14} />
                      {fav.address}
                    </p>
                  )}

                  {fav.usual_order && (
                    <div className="bg-black rounded-lg p-2 mb-3">
                      <p className="text-xs text-gray-500">Usual Order:</p>
                      <p className="text-sm">{fav.usual_order}</p>
                    </div>
                  )}

                  {fav.notes && (
                    <p className="text-xs text-gray-500 mb-3">{fav.notes}</p>
                  )}

                  <div className="flex gap-2 flex-wrap">
                    {fav.uber_eats_link && (
                      <a
                        href={fav.uber_eats_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 px-3 py-2 bg-[#06C167] text-white text-sm rounded-lg hover:opacity-80 text-center flex items-center justify-center gap-1"
                      >
                        <ExternalLink size={14} />
                        Uber Eats
                      </a>
                    )}
                    {fav.doordash_link && (
                      <a
                        href={fav.doordash_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 px-3 py-2 bg-[#FF3008] text-white text-sm rounded-lg hover:opacity-80 text-center flex items-center justify-center gap-1"
                      >
                        <ExternalLink size={14} />
                        DoorDash
                      </a>
                    )}
                    {fav.grubhub_link && (
                      <a
                        href={fav.grubhub_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 px-3 py-2 bg-[#F63440] text-white text-sm rounded-lg hover:opacity-80 text-center flex items-center justify-center gap-1"
                      >
                        <ExternalLink size={14} />
                        Grubhub
                      </a>
                    )}
                  </div>

                  {fav.order_count > 0 && (
                    <p className="text-xs text-gray-500 mt-3 text-right">
                      Ordered {fav.order_count} times
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Order History</h2>

          {orders.length === 0 ? (
            <div className="bg-black border border-gray-400 rounded-xl p-12 text-center hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
              <History size={48} className="mx-auto mb-4 text-gray-600" />
              <h3 className="text-lg font-semibold mb-2">No orders logged yet</h3>
              <p className="text-gray-400 mb-4">Start tracking your food orders</p>
              <button
                onClick={() => setShowLogModal(true)}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg"
              >
                Log Your First Order
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map(order => (
                <div
                  key={order.id}
                  className="bg-black border border-gray-400 rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold">{order.restaurant_name}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          order.delivery_service === 'uber_eats' ? 'bg-[#06C167]/20 text-[#06C167]' :
                          order.delivery_service === 'doordash' ? 'bg-[#FF3008]/20 text-[#FF3008]' :
                          order.delivery_service === 'grubhub' ? 'bg-[#F63440]/20 text-[#F63440]' :
                          'bg-gray-700 text-gray-400'
                        }`}>
                          {order.delivery_service?.replace('_', ' ') || 'Unknown'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400 mt-1">{order.items}</p>
                      {order.notes && (
                        <p className="text-xs text-gray-500 mt-1">{order.notes}</p>
                      )}
                      <p className="text-xs text-gray-600 mt-2">
                        {new Date(order.ordered_at).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      {order.total_amount && (
                        <p className="text-lg font-bold text-green-400">
                          ${order.total_amount.toFixed(2)}
                        </p>
                      )}
                      {order.rating && (
                        <div className="flex items-center gap-1 mt-1 justify-end">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={12}
                              className={i < order.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Favorite Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {editingFavorite ? 'Edit Favorite Restaurant' : 'Add Favorite Restaurant'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-800 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Restaurant Name *</label>
                <input
                  type="text"
                  value={newFavorite.name}
                  onChange={(e) => setNewFavorite(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="e.g., Chipotle"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Cuisine Type</label>
                <input
                  type="text"
                  value={newFavorite.cuisine}
                  onChange={(e) => setNewFavorite(prev => ({ ...prev, cuisine: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="e.g., Mexican"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Address</label>
                <input
                  type="text"
                  value={newFavorite.address}
                  onChange={(e) => setNewFavorite(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="e.g., 123 Main St, City"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Your Usual Order</label>
                <input
                  type="text"
                  value={newFavorite.usual_order}
                  onChange={(e) => setNewFavorite(prev => ({ ...prev, usual_order: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="e.g., Chicken bowl, no beans, extra guac"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Uber Eats Link</label>
                <input
                  type="url"
                  value={newFavorite.uber_eats_link}
                  onChange={(e) => setNewFavorite(prev => ({ ...prev, uber_eats_link: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="https://www.ubereats.com/..."
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">DoorDash Link</label>
                <input
                  type="url"
                  value={newFavorite.doordash_link}
                  onChange={(e) => setNewFavorite(prev => ({ ...prev, doordash_link: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="https://www.doordash.com/..."
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Grubhub Link</label>
                <input
                  type="url"
                  value={newFavorite.grubhub_link}
                  onChange={(e) => setNewFavorite(prev => ({ ...prev, grubhub_link: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="https://www.grubhub.com/..."
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Notes</label>
                <textarea
                  value={newFavorite.notes}
                  onChange={(e) => setNewFavorite(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 h-20 resize-none"
                  placeholder="Any notes about this restaurant..."
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 flex gap-3">
              <button
                onClick={handleCloseModal}
                className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={editingFavorite ? handleUpdateFavorite : handleAddFavorite}
                className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
                data-testid="save-favorite-btn"
              >
                {editingFavorite ? 'Save Changes' : 'Add Favorite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Order Modal */}
      {showLogModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Log Food Order</h2>
              <button
                onClick={() => setShowLogModal(false)}
                className="p-2 hover:bg-gray-800 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Restaurant Name *</label>
                <input
                  type="text"
                  value={newOrder.restaurant_name}
                  onChange={(e) => setNewOrder(prev => ({ ...prev, restaurant_name: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="e.g., Chipotle"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Items Ordered *</label>
                <input
                  type="text"
                  value={newOrder.items}
                  onChange={(e) => setNewOrder(prev => ({ ...prev, items: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="e.g., Chicken bowl, chips & guac"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Total Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newOrder.total_amount}
                  onChange={(e) => setNewOrder(prev => ({ ...prev, total_amount: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                  placeholder="e.g., 15.99"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Delivery Service</label>
                <select
                  value={newOrder.delivery_service}
                  onChange={(e) => setNewOrder(prev => ({ ...prev, delivery_service: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500"
                >
                  <option value="uber_eats">Uber Eats</option>
                  <option value="doordash">DoorDash</option>
                  <option value="grubhub">Grubhub</option>
                  <option value="pickup">Pickup</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setNewOrder(prev => ({ ...prev, rating: star }))}
                      className="p-1"
                    >
                      <Star
                        size={24}
                        className={star <= newOrder.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Notes</label>
                <textarea
                  value={newOrder.notes}
                  onChange={(e) => setNewOrder(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 h-20 resize-none"
                  placeholder="Any notes about this order..."
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setShowLogModal(false)}
                className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogOrder}
                className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
              >
                Log Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodPage;
